import { useState } from 'react';
import { formatDuration, formatUKForDisplay, getCounterpartyNumber } from '@marnic/shared';
import { api } from '../lib/api';
import { relativeTime } from '../lib/utils';
import { CallRecordingPlayer } from './CallRecordingPlayer';
import { CallAnalysisPanel } from './CallAnalysisPanel';
import { useRedial } from './useRedial';
import type { CallRow } from './useRecentCalls';

interface Props {
  selectedCall: CallRow | null;
  prevCall: CallRow | null;
  nextCall: CallRow | null;
  onSelect: (id: string) => void;
  onClose?: () => void;
}

export function CallReviewPanel({ selectedCall, prevCall, nextCall, onSelect, onClose }: Props): JSX.Element {
  const { redial, busy, error, canRedial } = useRedial();
  const [applyError, setApplyError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);

  const applyDisposition = async (code: string): Promise<void> => {
    if (!selectedCall) return;
    setApplyError(null);
    setApplying(true);
    try {
      await api.post(`/calls/${selectedCall.id}/disposition`, { code });
    } catch (err) {
      const c = (err as { code?: string }).code;
      setApplyError(c ?? 'Could not apply disposition');
    } finally {
      setApplying(false);
    }
  };

  if (!selectedCall) {
    return (
      <div className="rounded-md border border-dashed border-ink-200 p-4 text-center text-xs text-ink-500">
        Select a call to review.
      </div>
    );
  }

  const counterparty = getCounterpartyNumber(selectedCall);
  const name = selectedCall.lead
    ? `${selectedCall.lead.firstName} ${selectedCall.lead.lastName}${selectedCall.lead.company ? ` · ${selectedCall.lead.company}` : ''}`
    : null;

  return (
    <div className="rounded-md border border-ink-200 bg-white p-3 space-y-3 text-sm">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => prevCall && onSelect(prevCall.id)}
          disabled={!prevCall}
          className="btn-secondary !px-2 !py-1 text-xs disabled:opacity-40"
          title="Previous call"
        >
          ← Prev
        </button>
        <button
          type="button"
          onClick={() => nextCall && onSelect(nextCall.id)}
          disabled={!nextCall}
          className="btn-secondary !px-2 !py-1 text-xs disabled:opacity-40"
          title="Next call"
        >
          Next →
        </button>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="ml-auto btn-secondary !px-2 !py-1 text-xs"
            title="Close review"
          >
            ✕
          </button>
        )}
      </div>

      <div>
        <div className="font-medium">{name ?? formatUKForDisplay(counterparty)}</div>
        {name && <div className="text-xs font-mono text-ink-500">{formatUKForDisplay(counterparty)}</div>}
        <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-ink-500">
          <span className="capitalize">{selectedCall.direction}</span>
          <span>· {relativeTime(selectedCall.startedAt)}</span>
          <span>· {formatDuration(selectedCall.durationSeconds ?? 0)}</span>
          <span>· {selectedCall.disposition ?? selectedCall.status}</span>
        </div>
      </div>

      <CallRecordingPlayer callId={selectedCall.id} hasRecording={!!selectedCall.recordingUrl} />

      <CallAnalysisPanel callId={selectedCall.id} onApplyDisposition={(code) => void applyDisposition(code)} />
      {applying && <div className="text-[11px] text-ink-500">Applying disposition…</div>}
      {applyError && <div className="text-xs text-red-600">{applyError}</div>}

      {selectedCall.notes && (
        <div className="rounded bg-ink-50 p-2 text-xs whitespace-pre-wrap">{selectedCall.notes}</div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => void redial(selectedCall)}
          disabled={!canRedial || busy}
          className="btn-primary text-sm flex-1"
        >
          {busy ? 'Dialling…' : '📞 Redial'}
        </button>
      </div>
      {error && <div className="text-xs text-red-600">{error}</div>}
    </div>
  );
}
